import { useEffect, useRef, useCallback, useState } from 'react';
import { ComponentTrackingConfig } from './types';
import { logEvent } from '@eventbrite/legacy-infra.statsig';
import { track } from '@eventbrite/legacy-infra.datalayer-library';

export const DEFAULT_INTERSECTION_OPTIONS: IntersectionObserverInit = {
  root: null,
  threshold: 0, // Trigger as soon as any part is visible for fastest response
  rootMargin: '0px 0px -150px 0px', // 150px above the conversion bar
};

/**
 * Pure visibility hook - detects when a component becomes visible using Intersection Observer
 *
 * @param callback - Function to call when component becomes visible
 * @param options - Intersection Observer options
 * @returns Ref to attach to the DOM element
 */
export const useComponentVisibility = (
  callback: () => void,
  options: IntersectionObserverInit = DEFAULT_INTERSECTION_OPTIONS,
) => {
  const elementRef = useRef<HTMLDivElement>(null);
  // hasBeenVisible is used to execute the callback only once
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  const memoizedCallback = useCallback(callback, [callback]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasBeenVisible) {
        memoizedCallback();
        setHasBeenVisible(true);
      }
    }, options);

    const element = elementRef.current;

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [memoizedCallback, hasBeenVisible, options]);

  return elementRef;
};

/**
 * Enhanced visibility hook with tracking capabilities
 *
 * @param callback - Function to call when component becomes visible
 * @param trackingConfig - Optional tracking configuration for Heap/Statsig
 * @returns Ref to attach to the DOM element
 */
export const useComponentVisibilityWithTracking = (callback?: () => void, trackingConfig?: ComponentTrackingConfig) => {
  const memoizedCallback = useCallback(() => {
    callback?.();

    if (trackingConfig) {
      dispatchTrackingEvents(trackingConfig);
    }
  }, [callback, trackingConfig]);

  const options = trackingConfig?.intersectionOptions || DEFAULT_INTERSECTION_OPTIONS;

  return useComponentVisibility(memoizedCallback, options);
};

export const dispatchTrackingEvents = (config: ComponentTrackingConfig) => {
  if (config.heap) {
    track({
      eventName: config.heap.eventName,
      eventData: {
        eventBucketLabel: config.heap.eventBucketLabel,
      },
    });
  }

  if (config.statsig) {
    logEvent(config.statsig.eventName, null, config.statsig.params || null);
  }
};
